import { useAppDispatch, useAppSelector } from "@app/hooks";
import { useApi } from "@services/useApi";
import { ExistingExaminee, updateExamineesCount } from "@store/locationsSlice";
import {
    NewExamineeId,
    NewExamineeStepNames,
    resetExaminee
} from "../../../../admin/tabs/assessments/store/newExamineeSlice";
import { ChangeEvent, useEffect, useState } from "react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { LTableResults, LTableSearch, LBtn, LModal, Loader } from "@components";
import { FormProvider, useForm } from "react-hook-form";
import { searchExamineesListFilter } from "@fadmin/tabs/results/components/filterHelper";
import { NewAssessmentBody, NewAssessmentFooter } from "../../assessments/components";
import { ExamineesHeader } from "../../assessments/components/ExamineesHeader";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { RaceOptions } from "@store/generalTypes";
import { convertToSentenceCase } from "@helpers/stringHelper";

const formSteps = [NewExamineeStepNames.ContactInfo, NewExamineeStepNames.Details, NewExamineeStepNames.AssignedTo];

export const ManageExaminees = (): JSX.Element => {
    const validateMethods = useForm();
    const dispatch = useAppDispatch();
    const organization = useAppSelector((x) => x.organization);
    const { currentLocation, examinee, organizationAssessments } = useAppSelector((x) => ({
        currentLocation: x.orgLocations.currentLocation,
        examinee: x.newExaminee,
        organizationAssessments: x.organizationAssessments
    }));
    const { getLocationExaminees, loading, addExaminee } = useApi();
    const [data, setData] = useState<ExistingExaminee[]>([]);
    const [existingExaminees, setExistingExaminees] = useState<ExistingExaminee[]>([]);
    const [newExamineeOpen, setNewExamineeOpen] = useState(false);
    const { isClinicalAdmin } = useUserPermission({ useCurrentLocation: true });
    const [currentStep, setCurrentStep] = useState(0);
    const [newExamineeId, setNewExamineeId] = useState<NewExamineeId>();

    const handleGetExaminees = async (): Promise<void> => {
        const locationExaminees = await getLocationExaminees(Number(currentLocation!.id));
        setData(locationExaminees.items);
        setExistingExaminees(locationExaminees.items);
        dispatch(
            updateExamineesCount({
                examineesCount: locationExaminees.totalCount ?? 0
            })
        );
    };

    useEffect(() => {
        setData([]);
        void handleGetExaminees();
    }, [organization, newExamineeId, currentLocation]);

    const toggleNewExaminee = (): void => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        } else {
            setCurrentStep(0);
            dispatch(resetExaminee());

            setNewExamineeOpen(!newExamineeOpen);
        }
    };

    const columnHelper = createColumnHelper<ExistingExaminee>();
    const baseColumns: Array<ColumnDef<ExistingExaminee, any>> = [
        columnHelper.accessor("lastName", {
            header: () => "Last Name",
            cell: ({ row }) => <span>{convertToSentenceCase(row.original.lastName)}</span>
        }),
        columnHelper.accessor("firstName", {
            header: () => "First Name",
            cell: ({ row }) => <span>{convertToSentenceCase(row.original.firstName)}</span>
        }),
        columnHelper.accessor("email", {
            header: () => "Email",
            cell: (cell) => <a href={`mailto:${cell.row.original.email}`}>{cell.row.original.email ?? "-"}</a>
        }),
        columnHelper.accessor("age", {
            header: () => "Age"
        }),
        columnHelper.accessor("birthDate", {
            header: () => "DOB"
        }),
        columnHelper.accessor("sex", {
            header: () => "Sex",
            cell: ({ row }) => <span>{convertToSentenceCase(row.original.sex)}</span>
        }),
        columnHelper.accessor("location", {
            header: () => "Location"
        }),
        columnHelper.accessor("clinicianLastName", {
            header: () => "Assigned Clinician",
            cell: ({ row }) => (
                <span>
                    {row.original.clinicianTitle} {row.original.clinicianLastName} {row.original.clinicianFirstName}
                </span>
            )
        }),
        columnHelper.accessor("race", {
            header: () => "Race",
            cell: ({ row }) => {
                const parsedName = row.original.race
                    ? RaceOptions.find((x) => x.value === row.original.race)?.name
                    : undefined;
                if (!parsedName) {
                    return <></>;
                }

                return (
                    <span className="has-text-overflow-ellipsis">
                        {parsedName?.length > 20 ? `${parsedName?.slice(0, 20)}...` : parsedName}
                    </span>
                );
            }
        })
    ];

    const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        setData(searchExamineesListFilter(e.target.value, existingExaminees));
    };

    const handleNewExamineeContinue = async (): Promise<void> => {
        const nextCurrentStep = currentStep + 1;

        if (!formSteps[nextCurrentStep]) {
            const examineeId = await addExaminee(examinee.assignedTo!.location, examinee);
            setNewExamineeId(examineeId);
            dispatch(resetExaminee());
            setNewExamineeOpen(!newExamineeOpen);
        } else {
            setCurrentStep(nextCurrentStep);
        }
    };

    return (
        <>
            <FormProvider {...validateMethods}>
                <div>
                    <span style={{ position: "absolute", right: "1%" }} className="py-3">
                        <LBtn isBold disabled={!organizationAssessments.hasPurchases} onClick={toggleNewExaminee}>
                            + Add Examinee
                        </LBtn>
                    </span>
                    <LTableSearch
                        numRecords={data.length}
                        label="Search examinees"
                        handleSearch={handleSearch}
                        resultCountContainerSize="4"
                        resultsLabel="examinees"
                    />
                </div>
                {LTableResults<ExistingExaminee>(data, baseColumns)}
                {loading && <Loader />}
            </FormProvider>
            {/* NEW EXAMINEE */}
            {newExamineeOpen && (
                <LModal
                    onClose={toggleNewExaminee}
                    onConfirm={handleNewExamineeContinue}
                    footer={<NewAssessmentFooter loading={loading} saveButtonText="Save" />}
                    header={<ExamineesHeader isNew allowCreateNew={isClinicalAdmin} allowExisting={false} />}
                    width={currentStep === 1 ? "l" : "m"}
                    fullHeight={currentStep === 1}
                >
                    <NewAssessmentBody currentStep={formSteps[currentStep]} />
                </LModal>
            )}
        </>
    );
};
